import { useState } from 'react';
import { AlertDialog, AlertDialogContent } from '@/components/ui/alert-dialog';
import { Button } from '@/components/Button';
import CandidatesOfferCopyTargetContents from './CandidatesOfferCopyTargetContents';
import usePostCandidateEndpointBulkCopyToCandidates from '../api/usePostCandidateEndpointBulkCopyToCandidates';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';
import type { AgentApiCandidateEndpointsCopyTargetCandidate } from '@/types/serializers';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateProfileId: number;
  jobPostEndpointIds: number[];
  onSuccess: () => void;
};

export default function CandidatesOfferCopyModal({
  open,
  onOpenChange,
  candidateProfileId,
  jobPostEndpointIds,
  onSuccess,
}: Props) {
  const [step, setStep] = useState<'select' | 'confirm'>('select');
  const [selectedTargetCandidates, setSelectedTargetCandidates] = useState<
    AgentApiCandidateEndpointsCopyTargetCandidate[]
  >([]);
  const copyMutation = usePostCandidateEndpointBulkCopyToCandidates();

  const reset = () => {
    setStep('select');
    setSelectedTargetCandidates([]);
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    try {
      await copyMutation.mutateAsync({
        candidateProfileId,
        jobPostEndpointIds,
        targetCandidateProfileIds: selectedTargetCandidates.map((candidate) => candidate.id),
      });
      showSuccessToast('提案求人をコピーしました');
      reset();
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      showErrorToast('提案求人のコピーに失敗しました');
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) reset();
        onOpenChange(nextOpen);
      }}
    >
      <AlertDialogContent className="max-w-max border-0 p-0">
        {step === 'select' && (
          <div className="flex h-[720px] max-h-[80vh] w-[1100px] max-w-[95vw] flex-col rounded bg-white">
            <div className="flex shrink-0 items-center border-b border-solid border-border px-lg py-md">
              <p className="text-heading-xs text-main">提案求人のコピー先を選択</p>
            </div>
            <div className="hidden-scrollbar min-h-0 flex-1 overflow-y-auto p-lg">
              <CandidatesOfferCopyTargetContents
                candidateProfileId={candidateProfileId}
                selectedTargetCandidates={selectedTargetCandidates}
                setSelectedTargetCandidates={setSelectedTargetCandidates}
              />
            </div>
            <div className="flex shrink-0 items-center justify-end gap-md border-t border-solid border-border px-lg py-md">
              <Button color="secondary" onClick={handleClose}>
                キャンセル
              </Button>
              <Button
                onClick={() => setStep('confirm')}
                disabled={selectedTargetCandidates.length === 0}
              >
                コピー先を確認
              </Button>
            </div>
          </div>
        )}
        {step === 'confirm' && (
          <div className="flex w-[522px] max-w-[522px] flex-col overflow-hidden rounded bg-white shadow-layer3">
            <div className="flex shrink-0 items-center border-b border-solid border-border px-lg py-md">
              <p className="text-heading-xs text-main">提案求人コピー先の確認</p>
            </div>
            <div className="flex min-h-0 flex-col gap-md p-lg">
              <p className="text-body-md text-black">
                選択した求人を以下の求職者へコピーしてよろしいですか？
              </p>
              <div className="hidden-scrollbar flex max-h-[240px] w-full flex-col items-center gap-xs overflow-y-auto bg-gray px-lg py-lg">
                {selectedTargetCandidates.map((candidate) => (
                  <p
                    key={candidate.id}
                    className="max-w-full shrink-0 truncate text-heading-xxs text-main"
                  >
                    {candidate.name}
                    {candidate.age != null && `(${candidate.age})`}
                  </p>
                ))}
              </div>
            </div>
            <div className="flex shrink-0 items-center justify-end gap-md border-t border-solid border-border px-lg py-md">
              <Button color="secondary" onClick={() => setStep('select')}>
                キャンセル
              </Button>
              <Button onClick={handleSubmit} disabled={copyMutation.isPending}>
                {copyMutation.isPending ? '送信中...' : 'コピーする'}
              </Button>
            </div>
          </div>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
